import { clsx } from "clsx";
import React, { useCallback, useEffect, useRef } from "react";

import * as styles from "./Drawer.css";
import { useDrawerContext } from "./context";
import { DrawerBodyProps } from "./types";

const scrollEdgeDetectionPx = 5;

/**
 * Body component for the Drawer
 *
 * Contains the main content with scroll capabilities.
 * Features optional gradient fade effects at the top and bottom when content overflows.
 *
 * @param showScrollGradients - Whether to show scroll gradients
 * @returns {JSX.Element} The body component with scroll capability
 */
export const DrawerBody = React.forwardRef<HTMLDivElement, DrawerBodyProps>(
  ({ children, showScrollGradients = true, className, ...elementProps }, ref) => {
    const internalBodyRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [showTopGradient, setShowTopGradient] = React.useState(false);
    const [showBottomGradient, setShowBottomGradient] = React.useState(true);

    const bodyRef = ref && "current" in ref ? ref : internalBodyRef;

    const { setShowTopBorder, setShowBottomBorder } = useDrawerContext();

    const handleScroll = useCallback(() => {
      if (!bodyRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
      const showTop = scrollTop > scrollEdgeDetectionPx;
      if (showScrollGradients) {
        setShowTopGradient(showTop);
      }
      setShowTopBorder(showTop);

      const isAtBottom =
        Math.abs(scrollTop + clientHeight - scrollHeight) <
        scrollEdgeDetectionPx;

      const showBottom = !isAtBottom && scrollHeight > clientHeight;
      if (showScrollGradients) {
        setShowBottomGradient(showBottom);
      }
      setShowBottomBorder(showBottom);
    }, [
      setShowBottomBorder,
      setShowTopBorder,
      showScrollGradients,
      setShowTopGradient,
      setShowBottomGradient
    ]);

    useEffect(() => {
      if (!showScrollGradients) {
        setShowTopGradient(false);
        setShowBottomGradient(false);
      }

      if (bodyRef.current) {
        const { scrollHeight, clientHeight } = bodyRef.current;

        if (showScrollGradients)
          setShowBottomGradient(scrollHeight > clientHeight);

        setShowBottomBorder(scrollHeight > clientHeight);
      }

      handleScroll();

      const resizeObserver = new ResizeObserver(() => {
        handleScroll();
      });

      if (bodyRef.current) {
        resizeObserver.observe(bodyRef.current);
      }

      if (wrapperRef.current) {
        resizeObserver.observe(wrapperRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, [
      handleScroll,
      setShowBottomBorder,
      showScrollGradients,
      setShowTopGradient,
      setShowBottomGradient
    ]);

    return (
      <div ref={wrapperRef} className={clsx(styles.bodyWrapper, className)} {...elementProps}>
        <div
          className={styles.body}
          ref={bodyRef}
          onScroll={handleScroll}
          tabIndex={0}
          aria-label="Drawer content"
        >
          {showScrollGradients && (
            <div
              className={styles.bodyGradientTop}
              style={{ opacity: showTopGradient ? 1 : 0 }}
              aria-hidden="true"
            />
          )}
          {children}
          {showScrollGradients && (
            <div
              className={styles.bodyGradientBottom}
              style={{ opacity: showBottomGradient ? 1 : 0 }}
              aria-hidden="true"
            />
          )}
        </div>
      </div>
    );
  }
);
